import React, { useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Drawer,
  Container,
  IconButton,
  Typography,
  Avatar,
  Badge,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import Sidebar from "components/Sidebar/Sidebar";
import Footer from "components/Footer/Footer";
import routes from "../routers";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../context/actions/user";
import { useQuery } from "react-query";
import ApiClient from "../api-client";

const drawerWidth = 240;

function AdminLayout(props) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainPanel = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [profileName, setProfileName] = useState("A");
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [notifications, setNotifications] = useState({ count: 0, records: [] });

  // Handle Sidebar Toggle
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    // Scroll to top on route change
    if (mainPanel.current) {
      mainPanel.current.scrollTop = 0;
    }
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [location]);

  // Fetch Notifications
  const fetchNotifications = async () => {
    try {
      const response = await ApiClient.get(
        "api/users/get-notification?page=1&limit=5&status=unread",
        "",
        true,
        dispatch
      );
      if (response?.status) {
        setNotifications({
          count: response?.body?.totalRecords || 0,
          records: response?.body?.result || [],
        });
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setNotifications({ count: 0, records: [] });
    }
  };

  useQuery(["users/get-notification"], fetchNotifications, {
    refetchInterval: 5000,
    enabled: !!user?._id,
  });

  // Update Profile Initials
  useEffect(() => {
    if (user?.firstName && user?.lastName) {
      setProfileName(`${user.firstName[0]}${user.lastName[0]}`);
    }
  }, [user]);

  // Menu Handlers
  const handleProfileMenuOpen = (event) => setProfileAnchor(event.currentTarget);
  const handleProfileMenuClose = () => setProfileAnchor(null);

  const handleNotificationsOpen = (event) => setNotificationAnchor(event.currentTarget);
  const handleNotificationsClose = () => setNotificationAnchor(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* Navbar with Sidebar Toggle & Icons */}
      <AppBar position="fixed" sx={{ color: "#121212", boxShadow: "none", borderBottom: '1px solid #efefef', backgroundColor: "white", zIndex: 1201 }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Sidebar Toggle Button */}
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>

          {/* Right Side: Notifications, Help, Logout, Profile */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton color="inherit" onClick={handleNotificationsOpen}>
                <Badge badgeContent={notifications.count} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Help */}
            <Tooltip title="Help">
              <IconButton color="inherit">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            {/* Logout */}
            <Tooltip title="Logout">
              <IconButton color="inherit" onClick={handleLogout}>
                <LogoutIcon />
              </IconButton>
            </Tooltip>

            {/* Profile */}
            <Tooltip title="Profile">
              <IconButton color="inherit" onClick={handleProfileMenuOpen}>
                <Avatar sx={{ bgcolor: "#efefef", color: "#ea6d58", width: 36, height: 36 }}>
                  {profileName}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar as a Drawer */}
      <Drawer
        variant="permanent"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": { width: drawerWidth, boxSizing: "border-box" },
        }}
      >
        <Sidebar {...props} routes={routes} />
      </Drawer>

      {/* Main Content Panel */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          height: "100%",
          p: 3,
          mt: 8,
        }}
        ref={mainPanel}
      >
        <Container sx={{ flexGrow: 1 }}>
          <Routes>
            {routes.map((route) =>
              route.subNav
                ? route.subNav.map((subRoute, key) => (
                    <Route path={subRoute.path} element={subRoute.component} key={key} />
                  ))
                : routes.map((prop, key) => (
                    <Route path={prop.path} element={prop.component} key={key} exact />
                  ))
            )}
          </Routes>
        </Container>

        {/* Footer (Always at the Bottom) */}
        <Box component="footer" sx={{ width: "100%", mt: "auto" }}>
          <Footer />
        </Box>
      </Box>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationsClose}
      >
        {notifications.records.length > 0 ? (
          notifications.records.map((notif, index) => (
            <MenuItem key={index} onClick={handleNotificationsClose}>
              {notif.message}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>No new notifications</MenuItem>
        )}
      </Menu>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileMenuClose}
      >
        <MenuItem onClick={() => navigate("/profile")}>
          <AccountCircleIcon sx={{ marginRight: 1 }} />
          Profile
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ marginRight: 1 }} />
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default AdminLayout;
